import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {Calc, Coppia} from "@commons/mapping/Calc";
import {cloneDeep} from "lodash";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {PresenzePianificazioneRecord} from "@commons/mapping/PresenzePianificazioneRecord";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {PresenzeGiustificativo} from "@commons/mapping/PresenzeGiustificativo";

type PrevistoPianificazione = {
    intervalli: CoppiaTimbrature[] | undefined, // un array di CoppiaTimbrature, rappresentanti gli intervalli orari lavorati
    hasEccedente: boolean,
    hasMancante: boolean,
    hasFuoriSede: boolean,
    hasErrorEccedente: boolean,
    hasErrorMancante: boolean,
    hasErrorFuoriSede: boolean,
};

export class Utils {
    /**
     * Restituisce un istanza di <PrevistoPianificazione> per il giorno e l'azienda indicata.
     * L'oggetto restituito contiene anche usa serie di flag utili alla diagnostica della giornata.
     * @param idAzienda
     * @param day
     * @param giustificativi
     */
    static getPrevistoPianificazione = (idAzienda: number | null, day: PresenzeGiornataPlusRecord, giustificativi: GiustificativoRecord[]): PrevistoPianificazione => {
        const operaDaMaster = idAzienda === day.idAziendaMaster;
        const result: PrevistoPianificazione = {
            intervalli: undefined,
            hasEccedente: false,
            hasMancante: false,
            hasFuoriSede: false, // Sia per indicare una pianificazione verso altra sede, sia per indicare la presenza DA altra sede
            hasErrorEccedente: false,
            hasErrorMancante: false,
            hasErrorFuoriSede: false,
        };
        const coppiePrevisto: Coppia[] = [];
        let coppieEccedente: Coppia[] = [];
        let coppieMancante: Coppia[] = [];
        let coppieFuoriSede: Coppia[] = [];
        let coppieInSede: Coppia[] = [];

        const timbrature = (day.previste && day.previste.length > 0) ? cloneDeep(day.previste).filter(coppia => !CoppiaTimbrature.isNull(coppia)) : [];
        for (const timbratura of timbrature) {
            coppiePrevisto.push(new Coppia(timbratura));
        }

        let giuMancantiCountSenzaIntervallo = 0; // Numero di giustificativi su mancante senza la specifica (dalle-alle)
        let hhMancanti = 0;
        if (day.causaliHH && day.causaliHH.length > 0) {
            for (const giu of day.causaliHH) {
                if (giu && giu.codGiu) {
                    const giustificativo = giustificativi.find(g => g.codGiu === giu.codGiu);
                    // RILEVO ECCEDENZE
                    if (!result.hasErrorEccedente && GiustificativoRecord.isEccedente(giustificativo)) {
                        result.hasEccedente = true;
                        const coppia = PresenzeGiustificativo.getIntervallo(giu);
                        if (coppia) {
                            coppieEccedente.push(new Coppia(coppia));
                        } else {
                            coppieEccedente = [];
                            result.hasErrorEccedente = true;
                        }
                    }

                    // RILEVO MANCANTE
                    if (GiustificativoRecord.isMancante(giustificativo)) {
                        hhMancanti += giu.quantita;
                        result.hasMancante = true;
                        const coppia = PresenzeGiustificativo.getIntervallo(giu);
                        if (coppia) {
                            coppieMancante.push(new Coppia(coppia));
                        } else {
                            ++giuMancantiCountSenzaIntervallo;
                        }
                    }
                }
            }
        }

        if (idAzienda) {
            if (!operaDaMaster) { // Se sto valutando una unità locale...
                // Valuto voci di pianificazione che considero a tutti gli effetti ORE MANCANTI (se VERSO altra azienda)
                if (idAzienda === day.idAzienda) {
                    if (day.vociPianificazione && day.vociPianificazione.length > 0) {
                        for (const voce of day.vociPianificazione) {
                            result.hasFuoriSede = true;
                            result.hasMancante = true;
                            const coppie = PresenzePianificazioneRecord.getIntervallo(voce, day);
                            if (coppie) {
                                // eslint-disable-next-line no-loop-func
                                coppie.forEach(coppia => coppieFuoriSede.push(new Coppia(coppia)));
                            } else {
                                coppieFuoriSede = [];
                                result.hasErrorFuoriSede = true;
                            }
                        }
                    }
                }
                // Valuto voci di pianificazione che considero a tutti gli effetti ORE PREVISTE (se DA altra azienda)
                if (idAzienda !== day.idAzienda) {
                    if (day.vociPianificazione && day.vociPianificazione.length > 0) {
                        for (const voce of day.vociPianificazione) {
                            if (idAzienda === voce.idAzienda) { // Solo per pianificazioni su UL in esame
                                result.hasFuoriSede = true;
                                const coppie = PresenzePianificazioneRecord.getIntervallo(voce, day);
                                if (coppie) {
                                    // eslint-disable-next-line no-loop-func
                                    coppie.forEach(coppia => coppieInSede.push(new Coppia(coppia)));
                                } else {
                                    coppieInSede = [];
                                    result.hasErrorFuoriSede = true;
                                }
                            }
                        }
                    }
                }
            } else {
                if (day.vociPianificazione && day.vociPianificazione.length > 0) {
                    for (const voce of day.vociPianificazione) {
                        result.hasFuoriSede = true;
                        const coppie = PresenzePianificazioneRecord.getIntervallo(voce, day);
                        if (coppie) {
                            coppie.forEach(coppia => coppieFuoriSede.push(new Coppia(coppia)));
                        } else {
                            coppieInSede = [];
                            result.hasErrorFuoriSede = true;
                        }
                    }
                }
            }
        }

        const previsto = new Calc();
        for (const coppia of coppiePrevisto)
            previsto.add(coppia);

        if (giuMancantiCountSenzaIntervallo > 0) {
            if (hhMancanti * 60 === previsto.getCount()) {
                // Nel caso vi siano giustificativi senza specifica (dalle alle), se il totale mancante è pari al previsto allora ritengo l'intervallo del mancante pari a quello previsto
                coppieMancante = coppiePrevisto;
            } else {
                // In caso contrario, con almeno un giustificativo senza specifica (dalle alle) segnalo errore
                coppieMancante = [];
                result.hasErrorMancante = true;
            }
        }

        let flat: any[] = [];
        let mancante = new Calc();
        // computo mancante vs previsto
        if (coppieMancante.length) {
            for (const coppia of coppieMancante) {
                if (previsto.getCount(coppia.calc) === coppia.calc.getCount())  // verifico integrità su intervallo
                    mancante.add(coppia);
                else
                    result.hasErrorMancante = true; // Mancante fuori range!
            }
        }

        // Aggiungo eventuali straordinari (previsto PLUS)
        for (const coppia of coppieEccedente) {
            if (previsto.getCount(coppia.calc) === 0)  // verifico integrità su intervallo
                previsto.add(coppia);
            else
                result.hasErrorEccedente = true; // Eccedente coincidente con ordinario (anche in parte)
        }

        if (idAzienda) {
            if (!operaDaMaster && (idAzienda !== day.idAzienda)) {
                // Caso singola unità locale, il lavoratore arriva da un altra UL
                const fuoriSede = new Calc();
                if (!result.hasErrorFuoriSede && coppieInSede.length) {
                    for (const coppia of coppieInSede) {
                        if (previsto.getCount(coppia.calc) === coppia.calc.getCount() // verifico integrità su previsto PLUS
                            //&& mancante.getCount(coppia.calc) === 0  // verifico integrità su mancante
                        ) {
                            fuoriSede.add(coppia);
                        } else { // Fuori sede fuori range!
                            result.hasErrorFuoriSede = true;
                        }
                    }

                    if (mancante.getCount() > 0) {
                        mancante = mancante.invert();
                        flat = result.hasErrorFuoriSede ? [] : fuoriSede.getFlat(mancante);
                    } else
                        flat = result.hasErrorFuoriSede ? [] : fuoriSede.getFlat();
                }
            } else {
                // Caso singola unità locale con lavoratore appartenente a essa
                if (!operaDaMaster && (idAzienda === day.idAzienda)) {
                    // Solo se NON master, computo fuori sede
                    if (coppieFuoriSede.length) {
                        for (const coppia of coppieFuoriSede) {
                            if (previsto.getCount(coppia.calc) === coppia.calc.getCount() // verifico integrità su previsto
                                //&& mancante.getCount(coppia.calc) === 0  // verifico integrità su mancante
                            )
                                mancante.add(coppia);
                            else
                                result.hasErrorFuoriSede = true; // Mancante fuori range!
                        }
                    }
                } else if (operaDaMaster) { // Caso master
                    if (coppieFuoriSede.length) {
                        for (const coppia of coppieFuoriSede) {
                            if (previsto.getCount(coppia.calc) !== coppia.calc.getCount()) // verifico integrità su previsto
                                result.hasErrorFuoriSede = true; // Mancante fuori range!
                        }
                    }
                }

                if (mancante.getCount() > 0) {
                    mancante = mancante.invert();
                    flat = previsto.getFlat(mancante);
                } else
                    flat = previsto.getFlat();
            }
        }

        const previstoLavorato = new Calc(flat);
        result.intervalli = previstoLavorato.getIntervalli();
        return result;
    }

    static getPrevisto = (day: PresenzeGiornataPlusRecord): CoppiaTimbrature[] => {

        return (day.previste && day.previste.length > 0) ? cloneDeep(day.previste).filter(coppia => !CoppiaTimbrature.isNull(coppia)) : [];
    }
}